<template>
  <div class="comp-container comp-container__product-modal POS-f TRBL-0">
    <div class="row no-gutters justify-content-center">
      <div class="col col-md-6 col-lg-4">
        <div class="push"></div>
        <transition name="animation-modal">
          <div class="modal POS-r BDRS-m BGC-white" v-if="show">
            <div class="header POS-s">
              <button ref="close" class="close POS-a P-s BGC-light BDRS-100 C-dark ACTIVE-small BUTTON-reset"
                v-on:click="$emit('close')">
                <span class="material-icons-round">clear</span>
              </button>
            </div>

            <div class="image POS-s OV-h BDRS-m BGC-light" v-if="image || product.image">
              <div class="image--thumbnail POS-a TRBL-0 BGR-n BGP-c-c BGZ-c BGC-light" v-if="product.image"></div>
              <swiper class="image--gallery POS-a TRBL-0" :options="gallerySwiperOptions">
                <swiper-slide class="image--gallery-image BGR-n BGP-c-c BGZ-c"
                  :style="'background-image: url(' + image + '), url(' + product.image + ')'"></swiper-slide>
                <template v-if="gallery.length !== 0">
                  <swiper-slide class="image--gallery-image BGR-n BGP-c-c BGZ-c" v-for="(image, index) in gallery"
                    v-bind:key="'image#' + index" :style="'background-image: url(' + image + ')'"></swiper-slide>
                </template>
                <div class="image--gallery-pagination swiper-pagination" slot="pagination" v-show="gallery.length !== 0">
                </div>
              </swiper>
            </div>
            <div class="meta MB-l PT-m PR-l PB-m PL-l">
              <div class="name PR-xxl FZ-24 FW-b LH-140">{{ product.name }}</div>
              <div class="price">
                <template v-if="'sale_price' in product">
                  <template v-if="product.sale_price > product.price">
                    <span class="price FZ-14 C-grey">
                      ${{ product.sale_price.toLocaleString() }}
                    </span>
                  </template>
                  <template v-else>
                    <span class="price FZ-14 C-grey">
                      ${{ product.sale_price.toLocaleString() }}
                    </span>
                    <span class="original-price FZ-14 C-grey">
                      ${{ product.price.toLocaleString() }}
                    </span>
                  </template>
                </template>
                <template v-else>
                  <span class="price FZ-14 C-grey">
                    ${{ product.price.toLocaleString() }}
                  </span>
                </template>
              </div>
              <vue-markdown class="desc MT-l C-grey FZ-15 LH-140" v-if="(product.desc || desc)"
                :source="product.desc || desc" :anchor-attributes="{ target: '_blank', rel: 'nofollow' }"
                :html="false"></vue-markdown>
            </div>

            <div class="loader-container POS-r PT-xxl PB-xxl" v-if="loading">
              <div class="loader"><span></span><span></span><span></span></div>
            </div>

            <div class="customize">
              <div class="customize__section MB-l" v-for="(section, customizeIndex) in customizes"
                v-bind:key="'cs-' + customizeIndex">
                <div class="customize__meta POS-s PR-l PB-s PL-l PT-s BGC-white">
                  <div class="customize__name MB-xs FZ-16 FW-b LH-140">
                    {{ section.name }}
                  </div>
                  <div class="customize__desc FZ-14 LH-140 C-grey">
                    <template v-if="section.required">
                      <span>{{ $t('cart.required') }}</span>
                      <template v-if="section.max === section.min">
                        <span>{{ $t('cart.choose{num}', { num: section.max }) }}</span>
                      </template>
                      <template v-else>
                        <template v-if="section.min">
                          <span>{{ $t('product.minSelection{num}', { num: section.min }) }}</span>
                        </template>
                        <template v-if="section.max">
                          <span>{{ $t('product.maxSelection{num}', { num: section.max }) }}</span>
                        </template>
                      </template>
                    </template>
                    <template v-else>
                      <span>{{ $t('product.optional') }}</span>
                      <template v-if="section.max">
                        <span>{{ $t('product.maxSelection{num}', { num: section.max }) }}</span>
                      </template>
                    </template>
                  </div>
                </div>
                <div
                  class="customize__option P-m BDRS-m"
                  :class="{
                    'is-selected': customizeOptions[option.id] > 0 && !isOptionDisabled(option),
                    'is-sold-out': option.soldOut,
                    'is-disabled': isOptionDisabled(option),
                  }"
                  v-for="(option, optionIndex) in section.options"
                  v-bind:key="'cs-option-' + optionIndex"
                  v-on:click.stop="selectCustomizeOption(section, option)"
                >
                  <div class="customize__check MR-m BDRS-100 BGC-light">
                    <span class="material-icons-round M-xxs icon">check</span>
                  </div>
                  <div class="customize__content">
                    <div class="customize__name MR-s">
                      {{ option.name }}
                      <transition name="animation-slide-in-top" v-if="option.multiple">
                        <div class="customize__count MT-xxs" v-on:click.stop v-if="customizeOptions[option.id] !== 0">
                          <span class="material-icons-round M-xxs MR-xs icon C-light-grey">subdirectory_arrow_right</span>
                          <count
                            :value="customizeOptions[option.id]"
                            :mini="true"
                            :min="0"
                            :max="customizeOptionMaxAmount(section, option)"
                            @updateCount="updateCustomizeOptionAmount($event, option)"
                          />
                        </div>
                      </transition>
                    </div>
                    <div class="customize__soldout C-grey TA-r" v-if="option.soldOut">{{ $t('product.soldOut') }}</div>
                    <div class="customize__price C-grey TA-r" v-else>
                      {{ customizeOptionPrice(option) }}
                    </div>
                  </div>
                </div>
              </div>

              <template v-if="hasComment">
                <div class="customize__meta PL-l PR-l">
                  <div class="customize__name MB-xs FZ-16 FW-b LH-140">
                    {{ $t('brand.notes') }}
                  </div>
                  <div class="customize__desc FZ-14 LH-140 C-grey">
                    {{ $t('brand.specialRequirements') }}
                  </div>
                </div>
                <textarea
                  v-model.trim="comment"
                  class="customize__comment M-m P-m BDRS-m BGC-light"
                  aria-label="text"
                  :placeholder="$t('brand.enterTextHere')"
                  ></textarea>
              </template>
              <template v-if="hasRecipient">
                <div class="customize__meta PL-l PR-l">
                  <div class="customize__name MB-xs FZ-16 FW-b LH-140">
                    {{ $t('brand.recipient') }}
                  </div>
                  <div class="customize__desc FZ-14 LH-140 C-grey">
                    {{ $t('brand.enterRecipient') }}
                  </div>
                </div>
                <custom-input
                  id="recipient"
                  class="M-m P-m MR-m MB-s ML-m"
                  v-model.trim="recipient"
                  type="text"
                  autocomplete="off"
                  autocorrect="off"
                  autocapitalize="off"
                  spellcheck="false"
                  :label="$t('brand.recipient')"
                  :placeholder="$t('brand.maximumTextLimit')"
                  :has-error="recipientError()"
                >
                </custom-input>
                <span v-if="recipientError" class="PL-l PR-l FZ-14 LH-140 C-notice">{{ recipientErrorMessage }}</span>
              </template>
            </div>

            <div class="footer POS-s P-s BDRS-m" v-if="entered && !readonly">
              <count
                v-if="!(product.free_via_coupon || product.free_via_point)"
                class="MR-s MB-s"
                :value="amount"
                :min="min"
                :max="max"
                @updateCount="updateAmount"
              ></count>
              <yellow-button class="add-cart MR-s MB-s ACTIVE-small" :label="via === 'store' ? $t('brand.addToCart') : $t('brand.update')"
                :isLoading="addingToCart" :disabled="recipientError()" v-on:click.native="cta()">
                <template v-slot:right>
                  <span class="right">{{ $t('brand.subtotal') }}<br>${{ Number(totalPrice).toLocaleString() }}</span>
                </template>
              </yellow-button>
              <button class="delete POS-r MB-s ACTIVE-small" v-if="via === 'cart'" v-on:click="deleteItem()"><span
                  class="material-icons-round">delete</span>
                <div class="loader" v-if="deletingFormCart"><span></span><span></span><span></span></div>
              </button>
            </div>
          </div>
        </transition>
        <div class="push"></div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState, mapMutations, mapActions } from 'vuex';
import Axios from '@/helpers/axios';
import VueWaypoint from 'vue-waypoint';
import Swal from 'sweetalert2';
import { Swiper, SwiperSlide, directive } from 'vue-awesome-swiper';
import Cookies from 'js-cookie';
import VueMarkdown from 'vue-markdown';

import count from '@/components/form/Count.vue';
import yellowButton from '@/components/form/YellowButton.vue';
import customInput from '@/components/form/Input.vue';

Vue.use(VueWaypoint);

export default {
  components: {
    count,
    yellowButton,
    Swiper,
    SwiperSlide,
    VueMarkdown,
    customInput,
  },
  directives: {
    swiper: directive,
  },
  props: {
    via: {
      type: String,
      required: true,
    },
    product: {
      required: true,
    },
    orderType: {
      required: true,
    },
    orderTime: {
      required: false,
    },
    amount: {
      type: Number,
      default: 1,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      show: false,
      loading: true,
      entered: false,
      image: null,
      gallery: [],
      desc: '',
      min: 1,
      max: 99,
      customizes: [],
      customizeOptions: {},
      flatCustomizeOptions: {},
      hasComment: false,
      comment: null,
      hasRecipient: false,
      recipient: null,
      recipientErrorMessage: '',
      addingToCart: false,
      deletingFormCart: false,
      gallerySwiperOptions: {
        pagination: {
          el: '.swiper-pagination',
          type: 'fraction',
        },
      },
      productAmount: this.amount,
      blockedCustomizeOptions: [],
      lastSingleSelection: null,
    };
  },
  beforeMount() {
    if (this.via === 'store') {
      const data = {
        ...this.$route.query,
        brand_id: this.$route.params.brandId,
        store_id: this.$route.params.storeId,
        item_id: this.product.id,
      };

      if (this.orderTime) {
        data.orderDatetime = this.orderTime;
      }

      Axios({
        method: 'post',
        url: '/menu/item',
        data,
      })
        .then((response) => {
          if (response.data.code !== 500) {
            if (response.data.msg) {
              Swal.fire({
                text: response.data.msg,
              })
                .then(() => {
                  this.$emit('close');
                });
            } else {
              this.$emit('close');
            }
            return;
          }
          this.image = response.data.data.image;
          this.gallery = response.data.data.gallery || [];
          this.customizes = response.data.data.adds;
          this.customizes.forEach((section) => {
            section.options.forEach((option) => {
              this.$set(this.flatCustomizeOptions, option.id, option);
              this.$set(this.customizeOptions, option.id, 0);
            });
          });
          this.min = response.data.data.min;
          this.max = response.data.data.max;
          this.hasComment = response.data.data.has_comment;
          this.hasRecipient = response.data.data.has_recipient;
          this.loading = false;
          this.entered = true;

          this.trackViewItemGA();
          this.trackViewItemGTM();
          this.trackViewItemPixel();
        });
    } else if (this.via === 'cart') {
      const data = {
        ...this.$route.query,
        brand_id: this.$route.params.brandId,
        order_item_id: this.product.id,
      };

      Axios({
        method: 'post',
        url: '/cart/orderItem',
        data,
      })
        .then((response) => {
          if (response.data.code !== 500) {
            if (response.data.msg) {
              Swal.fire({
                text: response.data.msg,
              })
                .then(() => {
                  this.$emit('close');
                });
            } else {
              this.$emit('close');
            }
            return;
          }
          this.image = response.data.data.image;
          this.gallery = response.data.data.gallery || [];
          this.customizes = response.data.data.adds;
          this.customizes.forEach((section) => {
            section.options.forEach((option) => {
              this.$set(this.flatCustomizeOptions, option.id, option);
              this.$set(this.customizeOptions, option.id, option.selected || 0);
              if (this.customizeOptions[option.id] > 0) {
                this.updateBlockedCustomizeOptions(option);
              }
            });
          });
          this.min = response.data.data.min;
          this.max = response.data.data.max;
          this.hasComment = response.data.data.has_comment;
          this.comment = this.product.comment;
          this.hasRecipient = response.data.data.has_recipient;
          this.recipient = this.product.recipient;
          this.image = response.data.data.image;
          this.desc = response.data.data.desc;
          this.loading = false;
          this.entered = true;
        });
    }
  },
  mounted() {
    this.show = true;
  },
  methods: {
    ...mapMutations([
      'SET_CURRENT_ORDER',
      'REMOVE_FROM_SAVED_ORDERS',
      'SET_CURRENT_ORDER_DELIVERY',
      'SET_CART',
      'RESET_CART',
    ]),
    ...mapActions([
      'ADD_TO_CART',
    ]),
    recipientError() {
      const maxChineseChars = 5;
      const maxEnglishChars = 10;
      const specialCharPattern = /[^a-zA-Z0-9\u4e00-\u9fa5]/;

      if (this.recipient) {
        const chineseCharCount = (this.recipient.match(/[\u4e00-\u9fa5]/g) || []).length;
        const englishCharCount = this.recipient.length - chineseCharCount;

        if (specialCharPattern.test(this.recipient)) {
          this.recipientErrorMessage = this.$i18n.t('brand.avoidSpecialSymbols');
          return true;
        }

        if (chineseCharCount > maxChineseChars || englishCharCount > maxEnglishChars) {
          this.recipientErrorMessage = this.$i18n.t('brand.exceedLimit');
          return true;
        }
        this.recipientErrorMessage = '';
        return false;
      }

      this.recipientErrorMessage = '';
      return false;
    },
    selectOption($event) {
      $event.target.classList.add('is-selected');
    },
    updateBlockedCustomizeOptions(option) {
      if (option.block.length > 0) {
        option.block.forEach((id) => {
          if (!this.blockedCustomizeOptions.includes(id)) {
            this.blockedCustomizeOptions.push(id);
          }
        });
      }
    },
    isOptionDisabled(option) {
      return this.blockedCustomizeOptions.includes(option.id);
    },
    selectCustomizeOption(section, option) {
      if (this.readonly) return;
      if (option.soldOut) return; // 售完

      if (this.customizeOptions[option.id] === 0) { // 還未選擇
        if (section.max === 1) { // 如果是單選，模擬 radio input 行為
          section.options.forEach((sectionOption) => {
            this.customizeOptions[sectionOption.id] = 0;
          });

          // 清除同組單選的選項
          if (this.lastSingleSelection && this.lastSingleSelection.sectionId === section.id) {
            this.lastSingleSelection.block.forEach((id) => {
              const index = this.blockedCustomizeOptions.indexOf(id);
              if (index !== -1) {
                this.blockedCustomizeOptions.splice(index, 1);
              }
            });
          }

          this.customizeOptions[option.id] = 1;
          this.lastSingleSelection = {
            ...option,
            sectionId: section.id,
          };
        } else {
          if (this.selectedCustomizeSectionAmount[section.id] === section.max) {
            this.$root.$message.error(`「${section.name}」${this.$i18n.t('product.allowedMaxSelection{num}', { num: section.max })}`);
            return;
          }

          this.customizeOptions[option.id] = 1;
        }
        // 更新阻擋選項
        this.updateBlockedCustomizeOptions(option);
      } else if (this.customizeOptions[option.id] >= 1) { // 已選擇
        this.customizeOptions[option.id] = 0;
        // 取消阻擋選項
        if (option.block.length > 0) {
          option.block.forEach((id) => {
            const index = this.blockedCustomizeOptions.indexOf(id);
            if (index !== -1) {
              // 檢查所有阻擋選項有衝突的id
              const isOptionSelected = (opt) => opt !== option && this.customizeOptions[opt.id] > 0 && opt.block.includes(id);
              const isStillSelected = this.customizes.some((group) => group.options.some((opt) => isOptionSelected(opt)));

              if (!isStillSelected) {
                this.blockedCustomizeOptions.splice(index, 1);
              }
            }
          });
        }
      }
    },
    customizeOptionMaxAmount(section, option) {
      const currentAmount = this.customizeOptions[option.id];

      const sectionAmount = this.selectedCustomizeSectionAmount[section.id];

      if (option.max) return Math.min(option.max, currentAmount + (section.max - sectionAmount));

      return currentAmount + (section.max - sectionAmount);
    },
    updateCustomizeOptionAmount(value, option) {
      this.customizeOptions[option.id] = value;
    },
    customizeOptionPrice(option) {
      let { price } = option;

      if (this.customizeOptions[option.id] > 1) price *= this.customizeOptions[option.id];

      if (price > 0) {
        return `+$${price.toLocaleString()}`;
      }

      if (price === 0) {
        return '$0';
      }

      return `-$${Math.abs(price).toLocaleString()}`;
    },
    updateAmount(value) {
      this.productAmount = value;
    },
    cta() {
      const checkRequired = this.customizes.every((section) => {
        if (section.required) {
          const selected = this.selectedCustomize[section.id];
          if (selected.length === 0) {
            this.$root.$message.error(this.$i18n.t('product.required{name}', { name: section.name }));
            return false;
          }
        }
        return true;
      });

      if (!checkRequired) {
        return;
      }

      const checkMin = this.customizes.every((section) => {
        if (section.min && section.min > this.selectedCustomizeSectionAmount[section.id]) {
          this.$root.$message.error(`「${section.name}」${this.$i18n.t('product.requiredMinSelection{num}', { num: section.min })}`);
          return false;
        }
        return true;
      });

      if (!checkMin) {
        return;
      }

      if (this.via === 'store') {
        this.addToCart();
      } else if (this.via === 'cart') {
        this.updateItem();
      }
    },
    addToCart() {
      const data = {
        ...this.$route.query,
        brand_id: this.$route.params.brandId,
        store_id: this.$route.params.storeId,
        orderType: this.orderType,
        item_id: this.product.id,
        count: this.productAmount,
        adds: this.flatSelectedCustomize,
      };

      if (this.currentOrder.id) {
        data.order_id = this.currentOrder.id;
      }

      if (this.orderTime) {
        data.orderDatetime = this.orderTime;
      }

      if (this.comment) {
        data.comment = this.comment;
      }

      if (this.recipient) {
        data.recipient = this.recipient;
      }

      if (Cookies.get('platform')) {
        data.platform = decodeURIComponent(Cookies.get('platform'));
      }

      this.addingToCart = true;

      Axios({
        method: 'post',
        url: '/cart/addItem',
        data,
      })
        .then((response) => {
          if (response.data.code !== 500) {
            if (response.data.msg) {
              Swal.fire({
                text: response.data.msg,
              })
                .then(() => {
                  if (response.data.clearOrder) {
                    this.REMOVE_FROM_SAVED_ORDERS({
                      brand: this.$route.params.brandId,
                      store: this.$route.params.storeId,
                    });
                    this.RESET_CART();
                  }

                  if (response.data.reload) {
                    window.location.reload();
                  }

                  if (response.data.redirect) {
                    this.$router.push(response.data.redirect);
                  }
                });
            } else {
              if (response.data.clearOrder) {
                this.REMOVE_FROM_SAVED_ORDERS({
                  brand: this.$route.params.brandId,
                  store: this.$route.params.storeId,
                });
                this.RESET_CART();
              }

              if (response.data.reload) {
                window.location.reload();
              }

              if (response.data.redirect) {
                this.$router.push(response.data.redirect);
              }
            }

            this.addingToCart = false;
            return;
          }

          if (response.data.code === 500) {
            this.$root.$message.success(this.$i18n.t('product.added'));

            this.SET_CURRENT_ORDER({
              brand: this.$route.params.brandId,
              store: this.$route.params.storeId,
              id: response.data.data.order.id,
              property: response.data.data.order.property || null,
            });

            if (response.data.data.order.delivery_provider) {
              const deliveryFeeData = {
                provider: response.data.data.order.delivery_provider,
                fee: response.data.data.order.delivery_fee,
                distance: response.data.data.order.distance,
                discount: response.data.data.order.delivery_fee_discount,
              };

              if (response.data.data.order.free_delivery) {
                deliveryFeeData.freeDelivery = response.data.data.order.free_delivery;
              }

              if (response.data.data.order.delivery_minimum) {
                deliveryFeeData.minimum = response.data.data.order.delivery_minimum;
              }

              this.SET_CURRENT_ORDER_DELIVERY(deliveryFeeData);
            }

            this.SET_CART(response.data.data.order);

            this.trackAddToCartGA();
            this.trackAddToCartGTM();
            this.trackAddToCartPixel();
            this.trackAddToCartLT();

            this.$emit('close');
          }
        });
    },
    updateItem() {
      const data = {
        ...this.$route.query,
        brand_id: this.$route.params.brandId,
        order_item_id: this.product.id,
        count: this.productAmount,
        adds: this.flatSelectedCustomize,
        comment: this.comment,
      };

      if (this.recipient) {
        data.recipient = this.recipient;
      }

      if (this.orderTime) {
        data.orderDatetime = this.orderTime;
      }

      this.addingToCart = true;

      Axios({
        method: 'post',
        url: '/cart/updateOrderItem',
        data,
      })
        .then((response) => {
          if (response.data.code !== 500) {
            if (response.data.msg) {
              Swal.fire({
                text: response.data.msg,
              })
                .then(() => {
                  if (response.data.clearOrder) {
                    this.REMOVE_FROM_SAVED_ORDERS({
                      brand: this.$route.params.brandId,
                      store: this.$route.params.storeId,
                    });
                    this.RESET_CART();
                  }

                  if (response.data.reload) {
                    window.location.reload();
                  }

                  if (response.data.redirect) {
                    this.$router.push(response.data.redirect);
                  }
                });
            } else {
              if (response.data.clearOrder) {
                this.REMOVE_FROM_SAVED_ORDERS({
                  brand: this.$route.params.brandId,
                  store: this.$route.params.storeId,
                });
                this.RESET_CART();
              }

              if (response.data.reload) {
                window.location.reload();
              }

              if (response.data.redirect) {
                this.$router.push(response.data.redirect);
              }
            }
            return;
          }

          this.$emit('updateCart', response.data.data);
          this.$emit('close');
        });
    },
    deleteItem() {
      const data = {
        ...this.$route.query,
        brand_id: this.$route.params.brandId,
        order_item_id: this.product.id,
        count: 0,
      };

      this.deletingFormCart = true;

      Axios({
        method: 'post',
        url: '/cart/updateOrderItem',
        data,
      })
        .then((response) => {
          if (response.data.code !== 500) {
            if (response.data.msg) {
              Swal.fire({
                text: response.data.msg,
              })
                .then(() => {
                  if (response.data.clearOrder) {
                    this.REMOVE_FROM_SAVED_ORDERS({
                      brand: this.$route.params.brandId,
                      store: this.$route.params.storeId,
                    });
                    this.RESET_CART();
                  }

                  if (response.data.reload) {
                    window.location.reload();
                  }

                  if (response.data.redirect) {
                    this.$router.push(response.data.redirect);
                  }
                });
            } else {
              if (response.data.clearOrder) {
                this.REMOVE_FROM_SAVED_ORDERS({
                  brand: this.$route.params.brandId,
                  store: this.$route.params.storeId,
                });
                this.RESET_CART();
              }

              if (response.data.reload) {
                window.location.reload();
              }

              if (response.data.redirect) {
                this.$router.push(response.data.redirect);
              }
            }
            return;
          }
          this.$emit('updateCart', response.data.data);

          this.trackRemoveFromCartGA();
          this.trackRemoveFromCartGTM();

          this.$emit('close');
        });
    },
    trackViewItemGA() {
      this.$gtag.event('view_item', {
        items: [this.trackItem],
      });
    },
    trackAddToCartGA() {
      this.$gtag.event('add_to_cart', {
        items: [this.trackItem],
      });
    },
    trackRemoveFromCartGA() {
      this.$gtag.event('remove_from_cart', {
        items: [this.trackItem],
      });
    },
    trackViewItemGTM() {
      if (this.purchaseTrackerGTM && this.$gtm && this.$gtm.enabled()) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          ecommerce: {
            detail: {
              products: [this.trackItem],
            },
          },
        });
      }
    },
    trackViewItemPixel() {
      if (window.fbq) {
        window.fbq(
          [
            'track',
            'ViewContent',
            this.trackPixelItem,
          ],
        );
      }
    },
    trackAddToCartGTM() {
      if (this.purchaseTrackerGTM && this.$gtm && this.$gtm.enabled()) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'addToCart',
          ecommerce: {
            currencyCode: 'TWD',
            add: {
              products: [this.trackItem],
            },
          },
        });
      }
    },
    trackAddToCartPixel() {
      if (window.fbq) {
        window.fbq(
          [
            'track',
            'AddToCart',
            this.trackPixelItem,
          ],
        );
      }
    },
    trackRemoveFromCartGTM() {
      if (this.purchaseTrackerGTM && this.$gtm && this.$gtm.enabled()) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'removeFromCart',
          ecommerce: {
            remove: {
              products: [this.trackItem],
            },
          },
        });
      }
    },
    trackAddToCartLT() {
      /* eslint no-underscore-dangle: ["error", { "allow": ["_lt"] }] */
      if (window._lt) {
        window._lt('send', 'cv', { type: 'AddToCart' }, ['b0b5f17b-ee86-4197-9da1-c73bfa7c58f7']);
      }
    },
  },
  computed: {
    ...mapState([
      'currentOrder',
      'cart',
    ]),
    trackItem() {
      return {
        id: this.product.id,
        name: this.product.name,
      };
    },
    totalPrice() {
      return ((this.product.sale_price || this.product.price || this.product.unit_price || 0) + this.customizePrice) * this.productAmount;
    },
    customizePrice() {
      let price = 0;

      Object.keys(this.customizeOptions).forEach((key) => {
        if (this.customizeOptions[key] > 0) {
          price += (this.customizeOptions[key] * this.flatCustomizeOptions[key].price);
        }
      });

      return price;
    },
    selectedCustomize() {
      const object = {};

      this.customizes.forEach((section) => {
        const array = [];

        section.options.forEach((option) => {
          if (this.customizeOptions[option.id] !== 0) {
            array.push(option.id);
          }
        });

        object[section.id] = array;
      });

      return object;
    },
    selectedCustomizeSectionAmount() {
      const object = {};

      this.customizes.forEach((section) => {
        let sectionAmount = 0;

        this.selectedCustomize[section.id].forEach((optionId) => {
          sectionAmount += this.customizeOptions[optionId];
        });

        object[section.id] = sectionAmount;
      });

      return object;
    },
    flatSelectedCustomize() {
      const array = [];

      Object.keys(this.customizeOptions).forEach((key) => {
        if (this.customizeOptions[key]) {
          array.push(`${key}:${this.customizeOptions[key]}`);
        }
      });

      return array;
    },
    trackPixelItem() {
      return {
        value: this.product.sale_price || this.product.price,
        currency: 'TWD',
        content_name: this.product.name,
        content_type: 'product',
        content_ids: [this.product.id],
      };
    },
  },
  watch: {
    recipient() {
      if (this.recipient !== null || this.recipient !== undefined) {
        this.recipientError();
      }
    },
  },
};
</script>

<style lang="sass" scoped>

.comp-container__product-modal
  width: 100%
  z-index: $z-index-modal
  overflow: auto

  background-color: rgba(0, 0, 0, .75)

  &::-webkit-scrollbar
    display: none

.push
  height: 10vh

.modal
  box-shadow: 0 1px 4px rgba(0, 0, 0, .25)

.header
  height: 0
  top: 0
  z-index: 1020

.close
  width: 40px
  height: 40px
  top: $size-m
  right: $size-m

  border: none

  &:active
    outline: none

.image
  padding-top: 100%

  &--thumbnail
    z-index: 10

  &--gallery
    z-index: 20

    ::v-deep .swiper-pagination
      left: auto
      right: $size-m
      bottom: $size-m
      width: auto
      padding: $size-xxs $size-s

      @extend .BDRS-l

      background-color: rgba(0, 0, 0, .25)

      @extend .FZ-12

      @extend .C-white

      @supports (backdrop-filter: saturate(180%) blur(5px))
        background-color: rgba(0, 0, 0, .25)

        backdrop-filter: saturate(180%) blur(5px)

.loader
  position: absolute
  top: 0
  right: 0
  bottom: 0
  left: 0
  display: flex
  justify-content: center
  align-items: center

  @extend .BDRS-99

  background-color: inherit

  span
    display: inline-block
    width: 10px
    height: 10px
    border-radius: 100%
    background-color: #575757
    margin: 0 $size-xxs
    opacity: 0

    &:nth-child(1)
      animation: opacitychange .5s ease-in-out infinite

    &:nth-child(2)
      animation: opacitychange .5s ease-in-out .166666667s infinite

    &:nth-child(3)
      animation: opacitychange .5s ease-in-out .333333334s infinite

    @keyframes opacitychange
      0%, 100%
        opacity: 0

      60%
        opacity: 1

.name
  word-break: break-all

.desc
  ::v-deep a
    color: currentcolor
    text-decoration: underline

  ::v-deep img
    max-width: 100%

  ::v-deep p
    margin-bottom: 0

.original-price
  text-decoration: line-through

.customize
  &__option
    display: flex
    align-items: center

    user-select: none
    cursor: pointer

    &.is-selected
      .customize__check
        @extend .BGC-brand

      .customize__name
        @extend .FW-b

      .customize__check
        @extend .BGC-brand

        transform: scale(1)

        .icon
          @extend .C-dark
          opacity: 1

      .customize__price
        @extend .FW-b

    &.is-sold-out
      pointer-events: none
      .customize__check
        transform: scale(.25)
    &.is-disabled
      pointer-events: none

      .customize__check
        transform: scale(.25)

      .customize__content
        text-decoration: line-through
        text-decoration-thickness: 2px
        text-decoration-color: $color-grey

  &__check
    width: 32px
    height: 32px
    min-width: 32px

    transform: scale(.75)
    transition: background $time-normal $cb, transform $time-fast $cb

    .icon
      @extend .C-light
      opacity: 0

      transition: opacity $time-normal $cb

  &__meta
    top: 0
    z-index: 1000

    span
      &::after
        content: '，'

      &:last-child
        &::after
          content: ''

  &__content
    flex-grow: 1
    display: flex
    align-items: center

  &__name
    flex-grow: 1

    word-break: break-all

  &__count
    display: flex
    align-items: center

  &__price
    min-width: 50px

  &__comment
    width: calc(100% - 32px)
    border: none
    box-shadow: inset 0 0 0 0 $color-light-grey

    transition: box-shadow $time-fast $cb

    &:focus
      box-shadow: inset 0 0 0 2px $color-light-grey

      outline: none

.footer
  display: flex
  bottom: 0
  padding-bottom: env(safe-area-inset-bottom)
  z-index: 1010

  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, white 100%)

  user-select: none

  .add-cart
    flex-grow: 1

  .delete
    width: 52px
    height: 52px
    padding: 14px $size-s

    border: none
    border-radius: 99px

    @extend .BGC-notice

    @extend .FW-b

    appearance: none
    outline: none

    color: white

    .loader
      position: absolute
      top: 0
      right: 0
      bottom: 0
      left: 0
      display: flex
      justify-content: center
      align-items: center

      @extend .BDRS-99

      background-color: inherit

      span
        display: inline-block
        width: 10px
        height: 10px
        border-radius: 100%
        background-color: white
        margin: 0 2px
        opacity: 0

        &:nth-child(1)
          animation: opacitychange .5s ease-in-out infinite

        &:nth-child(2)
          animation: opacitychange .5s ease-in-out .166666667s infinite

        &:nth-child(3)
          animation: opacitychange .5s ease-in-out .333333334s infinite

        @keyframes opacitychange
          0%, 100%
            opacity: 0

          60%
            opacity: 1
</style>
